import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { SingleSelectWithCustomValue } from 'common/form';
import { getProgramAndProviderOptions } from 'src/components/Referrals/ReferralGroupsPrograms/utils';
import { disabledCustomOptions } from 'src/components/Referrals/ReferralFormFields/OONGroupsSelector/utils';
import { sortGroupsByDistance } from 'src/components/Groups/utils';
import { connect } from 'react-redux';
import { paginateNetworkGroups } from 'src/common/utils/FeatureFlags/flags';

/** Resolves the current field value from a Redux Form field object or a plain value. */
const getFieldValue = (groupField) => {
  if (!groupField) return '';
  return groupField.value !== undefined ? groupField.value : groupField;
};

/** Returns the sorted/unsorted options list and placeholder based on pagination mode. */
const getOptionsConfig = (canPaginateNetworkGroups, options, originCoordinates) => {
  if (canPaginateNetworkGroups) {
    return { displayOptions: options, placeholderText: 'Choose a program' };
  }
  return {
    displayOptions: sortGroupsByDistance(options, originCoordinates),
    placeholderText: '',
  };
};

export function CaseOONGroupSelect({
  canPaginateNetworkGroups,
  debouncedSearchNetworkGroups,
  groupField,
  index,
  oonGroupFields,
  originCoordinates,
  removeGroupField,
  selectedGroupIds,
  shouldSearchOnChange,
  suggestedGroups,
}) {
  const handleRemoveGroupField = useCallback((selected) => {
    removeGroupField(selected, index);
  }, [removeGroupField, index]);

  const handleChange = useCallback((selected) => {
    // Update the Redux Form field value if groupField is provided
    if (groupField && groupField.onChange) {
      groupField.onChange(selected);
    }
    handleRemoveGroupField(selected);
  }, [groupField, handleRemoveGroupField]);

  const options = [
    ...getProgramAndProviderOptions(suggestedGroups, selectedGroupIds, originCoordinates, canPaginateNetworkGroups),
    ...disabledCustomOptions(oonGroupFields, true),
  ];

  const { displayOptions, placeholderText } = getOptionsConfig(canPaginateNetworkGroups, options, originCoordinates);
  const fieldValue = getFieldValue(groupField);

  return (
    <div className="case-oon-group-select">
      <SingleSelectWithCustomValue
        debouncedSearchNetworkGroups={debouncedSearchNetworkGroups}
        disableCustomValue
        canPaginateNetworkGroups={canPaginateNetworkGroups}
        id={`select-field-oon-group-${index}`}
        label="oon-group-select"
        hideLabel
        labelKey="displayName"
        valueKey="id"
        options={displayOptions}
        placeholder={placeholderText}
        resetOptions={false}
        searchPlaceholderValue={
          canPaginateNetworkGroups ?
            'Displaying closest 50 - Search for more' :
            ''
        }
        shouldSearchOnChange={shouldSearchOnChange}
        value={fieldValue}
        onChange={handleChange}
        onBlur={groupField?.onBlur}
      />
    </div>
  );
}

CaseOONGroupSelect.propTypes = {
  canPaginateNetworkGroups: PropTypes.bool.isRequired,
  debouncedSearchNetworkGroups: PropTypes.func.isRequired,
  groupField: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  index: PropTypes.number.isRequired,
  originCoordinates: PropTypes.array,
  oonGroupFields: PropTypes.array.isRequired,
  removeGroupField: PropTypes.func.isRequired,
  selectedGroupIds: PropTypes.array.isRequired,
  suggestedGroups: PropTypes.array.isRequired,
  shouldSearchOnChange: PropTypes.bool,
};

CaseOONGroupSelect.defaultProps = {
  groupField: {},
  originCoordinates: [],
  shouldSearchOnChange: true,
};

const mapStateToProps = (state) => ({
  canPaginateNetworkGroups: paginateNetworkGroups(state),
});

export default connect(mapStateToProps)(CaseOONGroupSelect);
